import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common/common.service';
import { CoreService } from 'src/app/services/core/core.service';
@Injectable({
  providedIn: 'root'
})
export class MainGuard implements CanActivate {
  /**
   * Full URL for the navigation (lazy parent guards must not rely on {@link ActivatedRouteSnapshot} private router state).
   */
  private fullNavigationUrl(state: RouterStateSnapshot | undefined, route: ActivatedRouteSnapshot): string {
    const raw = state?.url ?? (route as any)['_routerState']?.url ?? '';
    if (typeof raw !== 'string') {
      return '';
    }
    // Drop query and fragment: B2C sign-up returns to `/#id_token=...` outside MSAL, which must still match '/'.
    return raw.split(/[?#]/)[0];
  }

  /**
   * constructor
   * @param router
   */
  constructor(
    private readonly commonService: CommonService,
    private readonly msalGuard: MsalGuard,
    private readonly router: Router,
    private readonly coreService: CoreService
  ) {}

  /**
   * canActivate
   * @returns
   */
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const url = state?.url?.split(/[?#]/)[0];

    if (url === '/home') {
      return true;
    }
    if (this.msalGuard.canActivate(next, state)) {
      return this.checkUserPermission(next, state);
    } else {
      this.router.navigate([ 'home' ]);
      return false;
    }
  }

  /**
   * checkUserPermission
   * @returns
   */
  checkUserPermission(route: ActivatedRouteSnapshot, routerState?: RouterStateSnapshot): Observable<boolean> {
    const currentNavUrl = this.fullNavigationUrl(routerState, route);
    return this.commonService.userProfileData.pipe(
      switchMap((res: any) => {
        if (res) {
          const automotiveServices = res?.data?.userPermission?.automotiveServices;
          this.commonService.setPermission(automotiveServices);
          // eslint-disable-next-line max-len
          let automotiveService = false;
          if (Array.isArray(route.data['automotiveService'])) {
            automotiveService = automotiveServices?.some(x => route.data['automotiveService'].includes(x.automotiveServiceName));
          } else {
            automotiveService = automotiveServices?.some(x => x.automotiveServiceName === route.data['automotiveService']);
          }

          if (!automotiveService) {
            const allowedRoute = `/${this.getLandingRoute(res)}`;
            if (currentNavUrl === allowedRoute || currentNavUrl == '/accessDenied') {
              return of(true);
            }

            if (currentNavUrl == '/') {
              return of(this.router.createUrlTree([ '/home' ]));
            } else {
              return of(true);
            }
          }

          //

          // for now permission related stuff only valid for addenda auto or any user which also has access to addenda auto
          if (
            (Array.isArray(route.data['automotiveService']) && route.data['automotiveService'].includes('Addenda Auto')) ||
            route.data['automotiveService'] == 'Addenda Auto'
          ) {
            const redirectRoute = this.evaluateAutoPermission(res?.data?.userPermission, currentNavUrl);

            if (redirectRoute == '') {
              return of(true);
            }

            this.router.navigate([ redirectRoute ]);
            return of(false);
          }

          //

          //
          return of(automotiveService);
        } else {
          this.commonService.showLoading();
          return this.coreService.getUserProfile().pipe(
            switchMap((res) => {
              if (res) {
                this.commonService.hideLoading();
                this.commonService.userProfileData.next(res);
                if (res?.data.userProfileThumbnailImageBlobUrl || res?.data.userProfileImageBlobUrl) {
                  this.commonService.avatar.next({
                    image: res?.data.userProfileThumbnailImageBlobUrl ?? res?.data.userProfileImageBlobUrl
                  });
                }
                if (res?.data?.userPermission?.automotiveServices) {
                  const automotiveServices = res?.data?.userPermission?.automotiveServices;
                  this.commonService.setPermission(automotiveServices);
                  // eslint-disable-next-line max-len
                  const automotiveService = automotiveServices?.some(x => x.automotiveServiceName === route.data['automotiveService']);
                  if (!automotiveService) {
                    this.router.navigate([ 'home' ]);
                  }
                  return of(automotiveService);
                } else {
                  this.router.navigate([ 'home' ]);
                  return of(false);
                }
              } else {
                this.commonService.hideLoading();
                this.router.navigate([ 'home' ]);
                return of(false);
              }
            })
          );
        }
      })
    );
  }

  /**
   * get first landing page
   * @param menu
   * @returns
   */
  private getFirstAllowedRoute(menu: AutoMenuItem[], userPermissionList: any): string | null {
    const orgPackages = userPermissionList?.orgPackages;
    const automotiveServices = userPermissionList?.automotiveServices;
    for (const item of menu) {
      // 1. If item has a route & permission — check permission
      if (item.route && item.userLevelPermission?.length > 0) {
        //const allowed = this.permissionService.checkPermission(item.automotiveService ?? '', item.permissionName);
        let allowed = false;
        if (item.orgPackage) {
          const orgPackagePermissionName = item.orgPackagePermission[0];
          allowed = this.commonService.checkOrgPackagesPermission(orgPackages, item.orgPackage, orgPackagePermissionName);

          if (allowed) {
            if (item.userLevelPermission?.length > 0) {
              allowed = this.checkPermissions(item.userLevelPermission, automotiveServices);
            }
          }
        } else {
          allowed = this.checkPermissions(item.userLevelPermission, automotiveServices);
        }

        if (allowed) {
          return item.route;
        }
      }

      // 2. If the menu has children, recursively check
      if (item.children?.length) {
        const childRoute = this.getFirstAllowedRoute(item.children, userPermissionList);
        if (childRoute) {
          return childRoute;
        }
      }
    }

    return null; // No allowed route found
  }

  /**
   * check user level permissions
   * @param userPermissions
   */
  checkPermissions(userPermissions: Array<string>, automotiveServices: any): boolean {
    //GET PERMISSIONS FROM ALL AUTOMOTIVE SERVICES
    const allPermissionLists = [ ...new Set(automotiveServices?.flatMap(item => item.permissions)) ];
    const permissionGrant = allPermissionLists?.some((permission: any) => userPermissions.includes(permission.permissionName));

    return permissionGrant;
  }

  /**
   * get landing route
   * @param res
   * @returns
   */
  private getLandingRoute(res: any): string | null {
    const automotiveServices = res?.data?.userPermission?.automotiveServices;
    const orgPackages = res?.data?.userPermission?.orgPackages;
    const userDetail = res?.data?.roleDetail;
    const isRepairLite = userDetail?.some(r => r.automotiveServiceName === 'Addenda Repair' && r.labelName === 'Addenda Repair Lite');
    this.commonService.setPermission(automotiveServices);

    const repair = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda repair' && x.labelName != 'Addenda Repair Lite');
    const quote = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'carheal quote');
    const claim = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda claims');
    const claimSurveyor = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'claim surveyor');
    const fnol = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'fnol');
    const quoteAssessment = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'quote assessment');
    const inventory = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda inventory');
    const auto = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda auto');
    const health = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda health');
    const addendaFacility = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda servescape');
    const property = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda property');
    const hrm = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'hrms');
    const hasDashboardFeaturePackage = orgPackages
      ?.find(x => x.automotiveServiceName.toLowerCase() === 'analytics')
      ?.permissions?.some(x => x.permissionName?.toLowerCase() === 'dashboard');
    if (repair) {
      const repairRole = repair.roleName.toLowerCase();
      const quoteRole = quote?.roleName.toLowerCase();
      if (repairRole === 'bodyshop manager' || quoteRole === 'bodyshop manager') {
        const hasDashboardPermission =
          repair.permissions.some(x => x.permissionName === 'dashboard') || quote?.permissions.some(x => x.permissionName === 'dashboard');
        if (isRepairLite || !hasDashboardPermission) {
          return 'repair';
        } else if (!hasDashboardFeaturePackage) {
          return 'repair/booking-journal';
        }
      } else if (repair.roleName.toLowerCase() === 'service desk') {
        if (this.commonService.checkPermission('Addenda Repair', 'outbound.lounge')) {
          return 'repair/lounge';
        } else {
          return 'repair/workflow-lounge'; //workflow.lounge
        }
      } else if (repair.roleName.toLowerCase() === 'driver') {
        return 'repair/outbound';
      } else if (repair.roleName.toLowerCase() === 'technician') {
        return 'repair/technician-calendar';
      } else {
        return 'repair';
      }
    } else if (quote) {
      const quoteRole = quote?.roleName.toLowerCase();
      const hasDashboardPermission = quote.permissions.some(x => x.permissionName === 'dashboard');
      if (quoteRole === 'bodyshop manager' && hasDashboardPermission) {
        if (!hasDashboardFeaturePackage) {
          return 'quote';
        }
      } else {
        return 'quote';
      }
    } else if (claim || claimSurveyor) {
      return 'claim';
    } else if (fnol) {
      return 'fnol';
    } else if (quoteAssessment) {
      return 'case';
    } else if (auto) {
      if (auto.roleName.toLowerCase() === 'agent admin') {
        return 'ai-studio';
      } else {
        return 'auto';
      }
    } else if (inventory) {
      return 'inventory';
    } else if (health || addendaFacility || property) {
      return 'employee-management';
    } else if (hrm) {
      return 'hrm/attendance';
    }
    return null;
  }

  /**
   * evaluate auto permission
   */
  evaluateAutoPermission(userPermission: any, currentUrl: string): string {
    const automotiveServices = userPermission?.automotiveServices ?? [];
    const orgPackages = userPermission?.orgPackages ?? [];

    /* ============================================================
       1️⃣ HOME / ROOT → LANDING ROUTE
    ============================================================ */
    if (currentUrl === '/' || currentUrl === '/home') {
      const landing = this.getFirstAllowedRoute(autoMenuItems, userPermission);
      return landing ? landing : '/accessDenied';
      //return of(landing ? this.router.createUrlTree([ landing ]) : this.router.createUrlTree([ '/accessDenied' ]));
    }

    /* ============================================================
       2️⃣ FIND MENU ITEM FOR CURRENT URL
    ============================================================ */
    const menuItem = this.findMenuItemByRoute(autoMenuItems, currentUrl);

    // Route not controlled by menu → allow
    if (!menuItem) {
      return '';
      //return of(true);
    }

    const automotiveService = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda auto');
    if (automotiveService?.roleName?.toLowerCase() == 'auto admin') {
      if (menuItem.allowAdmin) {
        return '';
      }
    }

    /* ============================================================
       3️⃣ AUTOMOTIVE SERVICE CHECK
    ============================================================ */
    if (menuItem.automotiveService && !automotiveServices.some(s => menuItem.automotiveService?.includes(s.automotiveServiceName))) {
      return '/accessDenied';
    }

    /* ============================================================
       4️⃣ ORG PACKAGE PERMISSION
    ============================================================ */
    if (menuItem.orgPackage) {
      //const requiredPermission = menuItem.userLevelPermission?.[0];
      const requiredPermission = menuItem.orgPackagePermission?.[0];
      const allowed = this.commonService.checkOrgPackagesPermission(orgPackages, menuItem.orgPackage, requiredPermission);
      if (!allowed) {
        return '/accessDenied';
      }
    }

    /* ============================================================
       5️⃣ USER LEVEL PERMISSION
    ============================================================ */
    if (menuItem.userLevelPermission?.length) {
      const allowed = this.checkPermissions(menuItem.userLevelPermission, automotiveServices);
      if (!allowed) {
        return '/accessDenied';
      }
    }

    if (menuItem.onlyAdmin) {
      if (automotiveService?.roleName?.toLowerCase() != 'auto admin') {
        return '/accessDenied';
      }
    }

    return '';
  }

  /**
   * find menu by route
   * @param menu
   * @param url
   * @returns
   */
  private findMenuItemByRoute(menu: AutoMenuItem[], url: string): AutoMenuItem | null {
    for (const item of menu) {
      if (item.route === url) {
        return item;
      }
      if (item.children?.length) {
        const found = this.findMenuItemByRoute(item.children, url);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }
}

export const autoMenuItems: AutoMenuItem[] = [
  {
    name: 'home',
    route: '/auto/dashboard',
    //route: '/home',
    permissionName: 'auto.dashboard',
    automotiveService: [ 'Addenda Auto' ],
    userLevelPermission: [ 'auto.dashboard' ]
  },
  {
    name: 'bookings',
    route: '/booking-management',
    automotiveService: [ 'Addenda Auto' ],
    permissionName: 'bookingjournal.listing',
    userLevelPermission: [ 'bookingjournal.listing' ]
  },
  {
    name: 'catalog_management',
    route: '/catalog-management',
    automotiveService: [ 'Addenda Auto', 'Service Management' ],
    children: [
      {
        name: 'catalog',
        parent: 'catalog-management',
        route: '/catalog-management',
        service: 'catalog',
        userLevelPermission: [ 'catalog.listing' ],
        automotiveService: [ 'Addenda Auto', 'Service Management' ]
      },
      {
        name: 'workflows',
        parent: 'catalog-management',
        route: '/catalog-management/workflows',
        service: 'catalog',
        userLevelPermission: [ 'catalog.update' ],
        automotiveService: [ 'Addenda Auto', 'Service Management' ]
      }
    ]
  },
  {
    name: 'employee_management',
    route: '/employee-management',
    automotiveService: [ 'Addenda Auto', 'Service Management', 'Addenda Health', 'Addenda Property' ],
    children: [
      {
        name: 'employee',
        parent: 'employee-management',
        route: '/employee-management',
        automotiveService: [ 'Addenda Auto', 'Service Management' ]
      },
      {
        name: 'employee_calendar',
        parent: 'employee-management',
        route: '/employee-management/calendar',
        automotiveService: [ 'Addenda Auto', 'Service Management' ]
      }
    ]
  },
  {
    name: 'payment_management',
    route: '/payment-management',
    automotiveService: [ 'Addenda Auto', 'Service Management' ],
    children: [
      {
        name: 'payment',
        parent: 'payment_management',
        route: '/payment-management',
        automotiveService: [ 'Addenda Auto', 'Service Management' ]
      },
      {
        name: 'payment_gateway_logs',
        parent: 'payment_management',
        route: '/payment-management/payment-gateway-logs',
        automotiveService: [ 'Addenda Auto', 'Service Management' ]
      }
    ]
  },
  {
    name: 'content_management',
    route: '/content-management',
    automotiveService: [ 'Addenda Auto', 'Service Management' ],
    onlyAdmin: true,
    children: [
      {
        name: 'banners',
        parent: 'content_management',
        route: '/content-management/banner',
        onlyAdmin: true,
        automotiveService: [ 'Addenda Auto', 'Service Management' ]
      },
      {
        name: 'contact_us',
        parent: 'content_management',
        route: '/content-management/contact-us',
        onlyAdmin: true,
        automotiveService: [ 'Addenda Auto', 'Service Management' ]
      },
      {
        name: 'general_content',
        parent: 'content_management',
        route: '/content-management/general-content',
        onlyAdmin: true,
        automotiveService: [ 'Addenda Auto', 'Service Management' ]
      },
      {
        name: 'look_feel',
        parent: 'content_management',
        route: '/content-management/look-feel',
        onlyAdmin: true,
        automotiveService: [ 'Addenda Auto', 'Service Management' ]
      }
    ]
  },
  {
    name: 'crm',
    route: '/customer-management',
    automotiveService: [ 'Addenda Auto' ],
    orgPackage: 'Customer Management',
    //userLevelPermission: [ 'customer.management' ],
    orgPackagePermission: [ 'customer.management' ],
    children: [
      {
        name: 'dashboard',
        parent: 'crm',
        route: '/customer-management/dashboard',
        automotiveService: [ 'Addenda Auto' ]
      },
      {
        name: 'customers',
        parent: 'crm',
        route: '/customer-management',
        service: 'Customer Management',
        userLevelPermission: [ 'customer.listing' ],
        automotiveService: [ 'Addenda Auto' ]
      },
      {
        name: 'settings',
        parent: 'crm',
        route: '/customer-management/settings',
        automotiveService: [ 'Addenda Auto' ]
      }
    ]
  },
  {
    name: 'ai_studio',
    route: '/ai-studio/dashboard',
    automotiveService: [ 'Addenda Auto', 'Addenda Health' ],
    orgPackage: 'AI Studio',
    orgPackagePermission: [ 'ai.studio' ],
    allowAdmin: true,
    children: [
      {
        name: 'dashboard',
        parent: 'ai_studio',
        route: '/ai-studio/dashboard',
        automotiveService: [ 'Addenda Auto', 'Addenda Health' ],
        userLevelPermission: [ 'ai.studio.agent.dashboard' ],
        allowAdmin: true
      },
      {
        name: 'create_agent',
        parent: 'ai_studio',
        route: '/ai-studio/create',
        automotiveService: [ 'Addenda Auto', 'Addenda Health' ],
        userLevelPermission: [ 'ai.studio.agent.create' ],
        allowAdmin: true
      },
      {
        name: 'create_agent_legacy',
        parent: 'ai_studio',
        route: '/ai-studio/create-legacy',
        automotiveService: [ 'Addenda Auto', 'Addenda Health' ],
        userLevelPermission: [ 'ai.studio.agent.create' ],
        allowAdmin: true
      },
      {
        name: 'agent_list',
        parent: 'ai_studio',
        route: '/ai-studio/list',
        automotiveService: [ 'Addenda Auto', 'Addenda Health' ],
        userLevelPermission: [ 'ai.studio.agent.listing' ],
        allowAdmin: true
      },
      {
        name: 'playground',
        parent: 'ai_studio',
        route: '/ai-studio/playground',
        automotiveService: [ 'Addenda Auto', 'Addenda Health' ],
        allowAdmin: true
      },
      {
        name: 'ai_studio_plans_billing',
        parent: 'ai_studio',
        route: '/ai-studio/subscriptions',
        automotiveService: [ 'Addenda Auto', 'Addenda Health' ],
        userLevelPermission: [ 'ai.studio.agent.subscription' ]
      }
    ]
  },
  {
    name: 'ai_agents',
    route: '/ai-agent',
    automotiveService: [ 'Addenda Auto' ],
    children: [
      {
        name: 'conversation_logs',
        parent: 'ai_agents',
        route: '/ai-agent/conversation-logs',
        automotiveService: [ 'Addenda Auto' ]
      },
      {
        name: 'analytics',
        parent: 'ai_agents',
        route: '/ai-agent/analytics',
        automotiveService: [ 'Addenda Auto' ]
      }
    ]
  },
  {
    name: 'settings',
    route: '/settings',
    automotiveService: [ 'Addenda Auto' ],
    children: [
      {
        name: 'partners',
        parent: 'settings',
        route: '/settings/partners',
        automotiveService: [ 'Addenda Auto' ]
      },
      // {
      //   name: 'manage_user',
      //   parent: 'settings',
      //   route: '/settings/user-management',
      //   service: 'User Management',
      //   automotiveService: [ 'Addenda Auto' ]
      // },
      {
        name: 'subscriptions',
        parent: 'settings',
        route: '/settings/subscriptions',
        automotiveService: [ 'Addenda Auto' ]
      },
      {
        name: 'schedules',
        parent: 'settings',
        route: '/settings/schedules',
        automotiveService: [ 'Addenda Auto' ]
      },
      {
        name: 'manage_roles',
        parent: 'settings',
        route: '/settings/roles',
        automotiveService: [ 'Addenda Auto' ]
      }
    ]
  }
];

export interface AutoMenuItem {
  name: string;
  displayName?: string;
  route?: string; //route
  permissionName?: string;
  automotiveService?: string[];
  userLevelPermission?: string[];
  children?: AutoMenuItem[];
  parent?: string;
  service?: string;
  allowAdmin?: boolean;
  onlyAdmin?: boolean;
  orgPackagePermission?: any;
  orgPackage?: string; // indicates org package
}
